/**
 * Apartment Selection Handler
 * Manages selected apartments in session storage and UI updates
 */

const STORAGE_KEY = 'selected_apartments';

function getCurrentLanguage() {
	const htmlLang = document.documentElement.lang || '';
	if ( htmlLang.indexOf( 'en' ) === 0 ) {
		return 'en';
	}
	if ( htmlLang.indexOf( 'fi' ) === 0 ) {
		return 'fi';
	}
	return 'fi';
}

/**
 * Get selected apartment IDs from session storage
 *
 * @return {Array} Array of apartment IDs
 */
function getSelectedApartments() {
	const stored = sessionStorage.getItem( STORAGE_KEY );
	return stored ? JSON.parse( stored ) : [];
}

/**
 * Save selected apartment IDs to session storage
 *
 * @param {Array} apartments - Array of apartment IDs
 */
function saveSelectedApartments( apartments ) {
	sessionStorage.setItem( STORAGE_KEY, JSON.stringify( apartments ) );
}

/**
 * Update the application button counter
 */
function updateCounter() {
	const counter = document.querySelector( '.js-application-button-number' );
	if ( counter ) {
		const selectedApartments = getSelectedApartments();
		counter.textContent = selectedApartments.length;
	}
}

/**
 * Sync all heart buttons on the page with session storage state
 */
function syncAllHeartButtons() {
	const selectedApartments = getSelectedApartments();
	const buttons = document.querySelectorAll( '.apartment-card__overlay' );

	buttons.forEach( ( button ) => {
		const apartmentId = button.getAttribute( 'data-apartment-id' );
		if ( apartmentId ) {
			if ( selectedApartments.includes( apartmentId ) ) {
				button.classList.add( 'selected' );
			} else {
				button.classList.remove( 'selected' );
			}
		}
	} );
}

/**
 * Toggle apartment selection
 *
 * @param {string}      apartmentId - The apartment post ID
 * @param {HTMLElement} button      - The button element that was clicked
 */
function toggleApartmentSelection( apartmentId, button ) {
	const selectedApartments = getSelectedApartments();
	const index = selectedApartments.indexOf( apartmentId );

	if ( index > -1 ) {
		// Remove from selection
		selectedApartments.splice( index, 1 );
		button.classList.remove( 'selected' );
	} else {
		// Add to selection
		selectedApartments.push( apartmentId );
		button.classList.add( 'selected' );
	}

	saveSelectedApartments( selectedApartments );
	updateCounter();
}

/**
 * Initialize selected state for all apartment cards on page load
 */
function initializeSelectedStates() {
	const selectedApartments = getSelectedApartments();
	const buttons = document.querySelectorAll( '.apartment-card__overlay' );

	buttons.forEach( ( button ) => {
		const apartmentId = button.getAttribute( 'data-apartment-id' );
		if ( apartmentId && selectedApartments.includes( apartmentId ) ) {
			button.classList.add( 'selected' );
		}
	} );

	updateCounter();
}

/**
 * Fetch apartment data by ID
 *
 * @param {string} apartmentId - The apartment post ID
 * @return {Promise} Promise that resolves with apartment data
 */
async function fetchApartmentData( apartmentId ) {
	try {
		// Use custom REST endpoint that returns apartment data with ElasticPress fields
		const response = await fetch( `/wp-json/ilmarinen/v1/apartment/${ apartmentId }` );
		if ( ! response.ok ) {
			throw new Error( 'Failed to fetch apartment data' );
		}
		return await response.json();
	} catch ( error ) {
		return null;
	}
}

/**
 * Create apartment card element from template
 *
 * @param {Object} apartment - Apartment data object
 * @return {Element|null} Cloned and populated apartment card element
 */
function createApartmentCardElement( apartment ) {
	const template = document.getElementById( 'apartment-card-template' );
	if ( ! template ) {
		return null;
	}

	const currentLang = getCurrentLanguage();
	const clone = template.content.cloneNode( true );
	const apartmentId = apartment.id;

	// Set link href
	const linkElement = clone.querySelector( '.js-apartment-link' );
	if ( linkElement && apartment.link ) {
		linkElement.href = apartment.link;
	}

	// Set overlay button apartment ID and mark as selected
	const overlayButton = clone.querySelector( '.js-apartment-overlay' );
	if ( overlayButton ) {
		overlayButton.setAttribute( 'data-apartment-id', apartmentId );
		overlayButton.classList.add( 'selected' );
	}

	// Set image
	const imageUrl = apartment.featured_image_url || '';
	const imageElement = clone.querySelector( '.js-apartment-image' );
	if ( imageUrl && imageElement ) {
		imageElement.src = imageUrl;
		imageElement.alt = apartment.title || '';
	} else if ( imageElement ) {
		imageElement.style.display = 'none';
	}

	// Set title
	const titleElement = clone.querySelector( '.js-apartment-title' );
	if ( titleElement ) {
		titleElement.textContent = apartment.title || '';
	}

	// Set location
	const locationElement = clone.querySelector( '.js-apartment-location' );
	if ( locationElement ) {
		locationElement.textContent = apartment.area_name || '';
	}

	// Set meta (rooms and area)
	const metaElement = clone.querySelector( '.js-apartment-meta' );
	if ( metaElement ) {
		const rooms = apartment.flat_structure || '';
		const area = apartment.living_area || '';

		// Create span elements for meta items
		const roomsSpan = document.createElement( 'span' );
		roomsSpan.className = 'apartment-card__meta';
		roomsSpan.textContent = rooms;

		const areaSpan = document.createElement( 'span' );
		areaSpan.className = 'apartment-card__meta';
		areaSpan.textContent = `${ area } m²`;

		metaElement.appendChild( roomsSpan );
		metaElement.appendChild( areaSpan );
	}

	// Set availability
	const availabilityElement = clone.querySelector( '.js-apartment-availability' );
	if ( availabilityElement && apartment.vacancy === '1' ) {
		const availabilityTexts = {
			fi: 'Heti vapaa',
			en: 'Available now',
		};

		const lang = availabilityTexts[ currentLang ] ? currentLang : 'fi';
		availabilityElement.textContent = availabilityTexts[ lang ];
		availabilityElement.style.display = 'block';
	}

	// Set price
	const priceElement = clone.querySelector( '.js-apartment-price' );
	if ( priceElement ) {
		const rent = apartment.rent || '';
		priceElement.textContent = `${ rent } €/kk`;
	}

	return clone;
}

/**
 * Populate the application menu with selected apartments
 */
async function populateApplicationMenu() {
	const apartmentsContainer = document.querySelector( '.js-application-menu-apartments' );

	if ( ! apartmentsContainer ) {
		return;
	}

	const selectedApartments = getSelectedApartments();
	const introSelected = document.querySelector( '.js-application-menu-intro-selected' );
	const introNoneSelected = document.querySelector( '.js-application-menu-intro-none-selected' );

	// Show/hide intro messages and update counter
	if ( selectedApartments.length > 0 ) {
		if ( introSelected ) {
			introSelected.style.display = 'block';
			// Update the counter inside the intro message
			const counterInIntro = introSelected.querySelector( '.js-application-button-number' );
			if ( counterInIntro ) {
				counterInIntro.textContent = selectedApartments.length;
			}
		}
		if ( introNoneSelected ) {
			introNoneSelected.style.display = 'none';
		}
	} else {
		if ( introSelected ) {
			introSelected.style.display = 'none';
		}
		if ( introNoneSelected ) {
			introNoneSelected.style.display = 'block';
		}
		apartmentsContainer.innerHTML = '';
		return;
	}

	// Fetch and render apartment data
	apartmentsContainer.innerHTML = '<p>Loading...</p>';

	try {
		const apartmentDataPromises = selectedApartments.map( ( id ) => fetchApartmentData( id ) );
		const apartmentsData = await Promise.all( apartmentDataPromises );

		// Clear container
		apartmentsContainer.innerHTML = '';

		// Create and append apartment cards
		apartmentsData
			.filter( ( apartment ) => apartment !== null )
			.forEach( ( apartment ) => {
				const cardElement = createApartmentCardElement( apartment );
				if ( cardElement ) {
					apartmentsContainer.appendChild( cardElement );
				}
			} );
	} catch ( error ) {
		apartmentsContainer.innerHTML = '<p>Error loading apartments</p>';
	}
}

/**
 * Initialize apartment selection functionality
 */
export function apartmentSelectionInit() {
	document.addEventListener( 'DOMContentLoaded', function() {
		// Initialize selected states on page load
		initializeSelectedStates();

		// Add click event listeners to all apartment card overlay buttons
		document.addEventListener( 'click', ( event ) => {
			const button = event.target.closest( '.apartment-card__overlay' );
			if ( button ) {
				event.preventDefault();
				event.stopPropagation();

				const apartmentId = button.getAttribute( 'data-apartment-id' );
				if ( apartmentId ) {
					toggleApartmentSelection( apartmentId, button );

					// If button is inside the application menu, refresh the panel
					const isInPanel = button.closest( '.js-application-menu-apartments' );
					if ( isInPanel ) {
						populateApplicationMenu();
						// Sync all heart buttons on the page to reflect the removal
						syncAllHeartButtons();
					}
				}
			}
		} );

		// Populate application menu when the button is clicked
		const applicationButton = document.getElementById( 'application-button' );
		if ( applicationButton ) {
			applicationButton.addEventListener( 'click', () => {
				populateApplicationMenu();
			} );
		}

		// Update application link with apartment IDs when clicked
		const applicationLink = document.querySelector( '.js-application-menu-to-application' );
		if ( applicationLink ) {
			applicationLink.addEventListener( 'click', () => {
				const selectedApartments = getSelectedApartments();
				if ( selectedApartments.length > 0 ) {
					const baseUrl = applicationLink.href.split( '?' )[ 0 ];
					const apartmentIds = selectedApartments.join( ',' );
					applicationLink.href = `${ baseUrl }?apartments=${ apartmentIds }`;
				}
			} );
		}

		// Hakuvahti link - update href with current search parameters when clicked
		const hakuvahtiLink = document.getElementById( 'hakuvahti-button' );
		if ( hakuvahtiLink ) {
			hakuvahtiLink.addEventListener( 'click', () => {
				// Get current URL search parameters
				const currentParams = new URLSearchParams( window.location.search );

				// Get the base hakuvahti URL
				const baseUrl = hakuvahtiLink.href.split( '?' )[ 0 ];

				// Update href with current search parameters
				hakuvahtiLink.href = baseUrl + '?' + currentParams.toString();
			} );
		}
	} );
}
